import $ = require('jquery');
import DD = require('./DD');
import DragHelper = require('./DragHelper');
import DropZone = require('./DropZone');
/**
 * DragSource class
 *
 * Class for making an element a valid source for a drag and drop operation.
 * Uses an extended jquery ui draggable widget
 */
    class DragSource extends DD {
        /**
         * @event ddstart
         * @event dddrag
         * @event ddstop
         *
         * @param {$.Event} original event
         * @param {Object} the original ui hash (usually coming from a jquery ui draggable)
         * @param {DragSource} the source that was dragged
         */

        /**
         * Name of the widget to use (a jquery ui draggable or extension)
         */
        protected static draggableWidgetName = 'draggable';

        /**
         * CSS cursor to use when dragging over a valid drop zone
         */
        protected allowedCursor = 'move';
        /**
         * CSS cursor to use when the drop is not allowed
         */
        protected notAllowedCursor = 'not-allowed';

        private helper: DragHelper;

        constructor(element: JQuery|string|HTMLElement) {
            super(element);

            //create the draggable here cause we need it to exist when we call setHelper
            this.element[this.getDraggableWidgetName()]();
        }

        getDraggableWidgetName() {
            return DragSource.draggableWidgetName;
        }

        setDraggableOptions(o: JQueryUI.DraggableOptions) {
            this.element[this.getDraggableWidgetName()]('option', o);
        }

        /**
         * An initialization method that we call only when this instance
         * is added to the drag'n'drop manager. No need to bind all events
         * when we instantiate the class
         */
        init() {
            this.setDraggableOptions({
                appendTo: 'body',
                containment: 'document',
                distance: 5, // leave a little more buffer than the default 1px
                // IE doesn't behave correctly when delay is used and the draggable is left hanging sometimes
                delay: $.browser.msie ? 0 : 50,
                refreshPositions: false,
                cursor: null, // we manage this internally
                scroll: true,
                start: $.proxy(this.onDragStart, this),
                drag: $.proxy(this.onDrag, this),
                stop: $.proxy(this.onDragStop, this)
            });
        }

        /**
         * Tests if the source can be dropped over the given drop zone
         * @returns True if possible, and error message string or false if not
         */
        canBeDropped(dropZone: DropZone): string|boolean {
            if (!dropZone) {
                return false;
            }
            return true;
        }

        /**
         * Updates the visual feedback based on the zone being hovered
         */
        updateView(dropZone: DropZone) {
            let canBeDropped = this.canBeDropped(dropZone);

            if (this.helper) {
                if (canBeDropped !== true) {
                    this.getHelper().updateInvitation(false,
                        dropZone && typeof canBeDropped === 'string' ? canBeDropped : null);
                } else {
                    this.getHelper().updateInvitation(true);
                }
            }

            this.setCursor(canBeDropped === true ? this.allowedCursor : this.notAllowedCursor);
        }

        /**
         * Sets the cursor while dragging or resets the cursor when dragging ends
         * @param cursor a CSS cursor or null value
         */
        setCursor(cursor: string) {
            $('body').css('cursor', cursor ? cursor : '');
        }

        /**
         * Tries to drop onto a drop zone
         *
         * @returns whether the drop was made or cancelled
         */
        drop(dropZone: DropZone) {
            if (this.canBeDropped(dropZone) === true) {
                this.doDrop(dropZone);
                this.trigger('dddropped');
                return true;
            } else {
                this.cancelDrop(dropZone);
                this.trigger('ddcancel');
                return false;
            }
        }

        /**
         * Override this method to provide an actual action to be executed
         * when dropped
         * @abstract
         */
        doDrop(dropZone: DropZone) {
            // abstract
        }

        /**
         * Override this method to provide an action when the drop has been cancelled
         * @abstract
         */
        cancelDrop(dropZone: DropZone) {
            // abstract
        }

        /**
         * Callback method for creating the draggable helper element
         */
        createHelper() {
            return this.helper.createHelper(this.element);
        }

        setHelper(helper: DragHelper) {
            let optHelper: () => void = null;
            if (helper instanceof DragHelper) {
                this.helper = helper;
                optHelper = () => this.createHelper();
            } else {
                this.helper = null;
            }

            if (this.element.is(":ui-" + this.getDraggableWidgetName())) {
                this.setDraggableOptions({ 'helper': optHelper });
            }
        }

        getHelper() {
            return this.helper;
        }

        onDragStart(e: Event, ui: {}) {
            this.trigger('ddstart', e, [ui, this]);
        }

        onDrag(e: Event, ui: {}) {
            this.trigger('dddrag', e, [ui, this]);
        }

        onDragStop(e: Event, ui: {}) {
            this.setCursor(null);

            this.trigger('ddstop', e, [ui, this]);
        }

        destroy() {
            this.setHelper(null);
            if (this.element.is(":ui-" + this.getDraggableWidgetName())) {
                this.element[this.getDraggableWidgetName()]('destroy');
            }
            super.destroy();
        }
    }

export = DragSource;



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/common/js/dd/DragSource.ts
 **/